import { Autocomplete, Option, Select } from '@mui/joy';

import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDebounce } from 'use-debounce';

import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useDatasets, useRunRules } from '@/hooks/useSwr';

import { FieldConfig } from './Shared';

interface DatasetNameAndId {
  name: string;
  id: string;
}
const SelectDataset = ({
  value,
  onChange,
}: {
  value: DatasetNameAndId | null;
  onChange: (datasetName: string, datasetId: string) => void;
}) => {
  const { selectedTagIds } = useStoredResourceTags();
  const [searchInput, setSearchInput] = useState(value?.name ?? '');
  const [debouncedSearchInput] = useDebounce(searchInput, 300);

  const { data: datasets } = useDatasets({
    name_contains: debouncedSearchInput,
    tag_value_id: selectedTagIds,
  });

  const handleInputChange = (
    _: React.SyntheticEvent,
    newValue: DatasetNameAndId | null
  ) => {
    if (newValue) {
      onChange(newValue.name, newValue.id);
    }
  };

  return (
    <Autocomplete
      value={value}
      onChange={handleInputChange}
      placeholder="Select a dataset"
      getOptionLabel={(option) => option?.name || ''}
      options={datasets?.map(({ name, id }) => ({ name, id })) ?? []}
      inputValue={searchInput}
      onInputChange={(_, value) => setSearchInput(value)}
      style={{ zIndex: 200 }}
      size="sm"
    />
  );
};

export const PromptimConfig = () => {
  const { control, setValue, watch } = useFormContext();
  const datasetId = watch('config.dataset_id');
  const { data: evaluators, isLoading } = useRunRules(
    datasetId ? { type: 'dataset', dataset_id: datasetId } : null
  );

  return (
    <>
      <FieldConfig
        title="Dataset"
        description="Optionally, it can have train/dev/test splits to report separate metrics throughout the training process"
      >
        <Controller
          name="config.dataset_name"
          control={control}
          render={({ field }) => (
            <SelectDataset
              value={{ name: field.value, id: datasetId }}
              onChange={(datasetName, datasetId) => {
                field.onChange(datasetName);
                setValue('config.dataset_id', datasetId);
              }}
            />
          )}
        />
      </FieldConfig>

      <FieldConfig
        title="Evaluation Metric"
        description="The algorithm will optimize for the maximum score of this evaluator metric"
      >
        <Controller
          name="config.evaluator"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value?.[0]}
              onChange={field.onChange}
              placeholder={
                isLoading
                  ? 'Loading evaluators...'
                  : !evaluators || evaluators.length === 0
                  ? 'No evaluators for this dataset'
                  : 'Select an evaluation metric'
              }
              size="sm"
              disabled={isLoading || !evaluators || evaluators.length === 0}
            >
              {evaluators?.map((evaluator) => (
                <Option key={evaluator.id} value={evaluator.id}>
                  {evaluator.display_name}
                </Option>
              ))}
            </Select>
          )}
        />
      </FieldConfig>
    </>
  );
};
